const A11Y_MENU_TYPE = {
    MEGA: 'mega',
    GRID_CHOOSE_COLUMNS: 'gridChooseColumns',   // for future use
    STANDARD: 'standard'
};

/**
* Class to handle displaying dropdown button menus in an accessible way. This is implemented as
* a class so that we can have multiple instances of the menu on the same page. All you should need
* to do is construct your HTML menu and instantiate this class passing in your menu container ID.
* You should NOT need to make any explicit calls to show() or hide() the menu, unless for some
* reason you need to force open/close it.
*
* EXAMPLE USAGE:
*
*  $(document).ready(function () {
       new A11yMenuInstance('userProfileMenuDiv');
   });

  <div id="userProfileMenuDiv" class="ah-button-menu">
       <button aria-expanded="false" aria-controls="userProfileMenu" id="userProfileMenuBtn">
           <span class="sr-only">User Profile</span>
           <i class="fad fa-user-cog" aria-hidden="true"></i>
           <span class="icon fas fa-caret-down" aria-hidden="true"></span>
       </button>

       <ul class="ah-menu" id="userProfileMenu" style="right:55px; top:46px;" aria-labelledby="userProfileMenuBtn">
           <li id="lnkMyProfileForRole"><button onclick="ShowProfileWindow()">My Profile</button></li>
           <li id="lichangepwd"><button onclick="ShowChangePassword()">Change Password</button></li>
           <li id="frmlogout"><a href="#" onclick="logout()">Logout</a></li>
           <li id="frmlogoutsso"><a href="#" onclick="logoutSSO()">Logout SSO</a></li>
       </ul>
   </div>
*
* @param {string} menuContainerId ID of the entire menu container, incl. the trigger button and
*                                 the menu overlay. Should be the DIV element with the ah-button-menu class
*                                 e.g. <div id="searchMenuDiv" class="ah-button-menu">...</div>
* @param {string} [menuType=standard] string indicating what type of menu this is (valid values from A11Y_MENU_TYPE constant)
* @param {function} [canOpenCallback] function to execute prior to the menu open to prevent it from opening (return false to prevent opening)
* @param {function} [clickCallback] function to execute after the menu is displayed (use this to execute custom logic, e.g. hide/display menu items) 
*/
let A11yMenuInstance = class A11yMenuClass {
    #menuType = A11Y_MENU_TYPE.STANDARD;
    #menuContainerId;   // id of the container/div holding both the trigger button and menu overlay
    #container;         // container element
    #triggerButton;     // the button which triggers the menu overlay to show
    #menu;              // the menu overlay
    #firstMenuItem;
    #lastMenuItem;
    #mouseOutTimer;             // timer used to delay hiding of menu after mouse leaves
    #mouseOutHideDelay = 100;  // time (in ms) before menu is closed after mouse leaves
    #menuVisible = false;       // current visibility state of menu
    #canOpenCallback;
    #clickCallback;

    constructor(menuContainerId, menuType, canOpenCallback, clickCallback) {
        this.#menuContainerId = menuContainerId;
        this.#canOpenCallback = canOpenCallback;
        this.#clickCallback = clickCallback;

        if (menuType) {
            this.#menuType = menuType;
        }

        let _this = this;

        _this.#initMenu(menuType);
    }

    /**
     * Hide the menu overlay and updated the aria-expanded attribute accordingly
     */
    hideMenu() {
        this.#triggerButton.setAttribute('aria-expanded', false);
        this.#menuVisible = false;
    }

    /**
     * Show the menu overlay and update the aria-expanded attribute accordingly
     */
    showMenu() {
        let _this = this;

        if (this.#canOpenCallback && !this.#canOpenCallback()) {
            DevUtils.logDebug(this.#menuContainerId + ': menu prevented from opening due to canOpenCallback() logic', 'A11yMenu.showMenu()', { canOpenCallback: this.#canOpenCallback });
            return;
        }

        setTimeout(function () {
            // determine whether we need to show a double- or triple-wide menu...
            let numItemsInMenu = _this.#getNumMenuItems();

            if (numItemsInMenu > 20) {
                $('#' + _this.#menuContainerId + '.ah-button-menu ul.ah-menu').addClass('ah-menu-3x-wide');
            } else if (numItemsInMenu > 10) {
                $('#' + _this.#menuContainerId + '.ah-button-menu ul.ah-menu').addClass('ah-menu-2x-wide');
            } else {
                $('#' + _this.#menuContainerId + '.ah-button-menu ul.ah-menu').removeClass('ah-menu-3x-wide');
                $('#' + _this.#menuContainerId + '.ah-button-menu ul.ah-menu').removeClass('ah-menu-2x-wide');
            }

            _this.#triggerButton.setAttribute('aria-expanded', true);
            _this.#menuVisible = true;

            // hide any sneaky kendo containers like calendar or appt pop-ups
            $('.k-animation-container').hide();

            if (_this.#clickCallback) {
                DevUtils.logDebug(_this.#menuContainerId + ': executing clickCallback() function', 'A11yMenu.showMenu()', { clickCallback: _this.#clickCallback });
                _this.#clickCallback();
            }
        });
    }

    /////////////////////
    // private methods //
    /////////////////////
    #initMenu(menuType) {
        let _this = this;

        if (menuType === A11Y_MENU_TYPE.MEGA) {
            _this.#constructMegaMenu();
        } else {
            _this.#constructMenu();
        }
    }

    #constructMenu() {
        let _this = this;

        this.#container = document.querySelector('#' + this.#menuContainerId);

        this.#triggerButton = document.querySelector('#' + this.#menuContainerId + '.ah-button-menu > button');
        this.#menu = document.querySelector('#' + this.#menuContainerId + '.ah-button-menu .ah-menu');

        this.#firstMenuItem = document.querySelector('#' + this.#menuContainerId + '.ah-button-menu > ul > li:first-of-type > a, #' + this.#menuContainerId + '.ah-button-menu > ul > li:first-of-type > button');
        this.#lastMenuItem = document.querySelector('#' + this.#menuContainerId + '.ah-button-menu > ul > li:last-of-type > a,  #' + this.#menuContainerId + '.ah-button-menu > ul > li:last-of-type > button');

        DevUtils.logDebug(this.#menuContainerId, 'A11yMenu.constructMenu()',
            [{ 'container': this.#container }, { 'triggerButton': this.#triggerButton }, { 'menu': this.#menu }, { 'firstMenuItem': this.#firstMenuItem }, { 'lastMenuItem': this.#lastMenuItem }]);

        // check to see if there are any menu items visible... if not, remove the menu button from the DOM. This will only
        // work if the menu is instantiated AFTER any code which checks permissions and hides <li> elements after they were
        // added to the DOM, e.g. using $().ApplyRoleConfig()
        if (this.#getNumMenuItems() === 0) {
            if (DevUtils.isDevMode()) {
                DevUtils.logWarn('Menu [' + this.#menuContainerId + '] has no visible items... hiding trigger button', 'A11yMenu.constructMenu()');
            }

            if (this.#container) {
                this.#container.remove();
            }

            return;
        }

        // Show the menu on mouse hover of the trigger
        this.#triggerButton.addEventListener('mouseover', function (e) {
            _this.#mouseover(e);
        });

        // Hide the menu when mouse hover leaves both the trigger and menu fly-out,
        this.#container.addEventListener('mouseleave', function (e) { _this.#mouseout(e); });

        // Hide the menu when the user tabs out of it
        this.#menu.addEventListener('keydown', function (e) {
            _this.#triggerKeydownHandler(e);
        });

        // Toggle the menu when the trigger is activated (works for both mouse and keyboard users)
        this.#triggerButton.addEventListener('click', function (e) {
            _this.#toggleMenu(e);
        });

        this.#container.addEventListener('focusout', function (e) {
            const nextFocusedElement = e.relatedTarget;
            if (!nextFocusedElement || !_this.#container.contains(nextFocusedElement)) {
                _this.hideMenu();
            }
        });
    }

    #constructMegaMenu() {
        let _this = this;

        this.#container = document.querySelector('#' + this.#menuContainerId);

        this.#triggerButton = document.querySelector('#' + this.#menuContainerId + '.ah-button-mega-menu > button');
        this.#menu = document.querySelector('#' + this.#menuContainerId + '.ah-button-mega-menu .ah-mega-menu');

        let firstItemSelector = '#' + this.#menuContainerId + '.ah-button-mega-menu > .ah-mega-menu > .ah-mega-menu-outer-list .ah-mega-menu-list-group:first-of-type .ah-menu-section:first-of-type span[role="listitem"]:first-of-type div[role="link"]:first-of-type, ' +
            '#' + this.#menuContainerId + '.ah-button-mega-menu > .ah-mega-menu > .ah-mega-menu-outer-list .ah-mega-menu-list-group:first-of-type .ah-menu-section:first-of-type span[role="listitem"]:first-of-type div[role="button"]:first-of-type';
        this.#firstMenuItem = document.querySelector(firstItemSelector);

        let lastItemSelector = '#' + this.#menuContainerId + '.ah-button-mega-menu > .ah-mega-menu .ah-mega-menu-outer-list .ah-mega-menu-list-group:last-of-type .ah-menu-section:last-of-type span[role="listitem"]:last-of-type div[role="link"]:last-of-type, ' +
            '#' + this.#menuContainerId + '.ah-button-mega-menu > .ah-mega-menu .ah-mega-menu-outer-list .ah-mega-menu-list-group:last-of-type .ah-menu-section:last-of-type span[role="listitem"]:last-of-type div[role="button"]:last-of-type';
        this.#lastMenuItem = document.querySelector(lastItemSelector);

        DevUtils.logDebug(this.#menuContainerId, 'A11yMenu.constructMegaMenu()',
            [{ 'container': this.#container }, { 'triggerButton': this.#triggerButton }, { 'menu': this.#menu }, { 'firstMenuItem': this.#firstMenuItem }, { 'lastMenuItem': this.#lastMenuItem }]);

        // Hide the menu when the user tabs out of it
        this.#menu.addEventListener('keydown', function (e) { _this.#triggerKeydownHandler(e); });

        // Toggle the menu when the trigger is activated (works for both mouse and keyboard users)
        this.#triggerButton.addEventListener('click', function (e) { _this.#toggleMenu(e); });

        // Hide the menu when mouse hover leaves both the trigger and menu fly-out,
        this.#container.addEventListener('mouseleave', function (e) {
            _this.#mouseout(e);
        });
    }

    #getNumMenuItems() {
        return $('button, a', '#' + this.#menuContainerId + '.ah-button-menu ul.ah-menu li').length;
    }

    #mouseout(e) {
        let _this = this;
        // add a short delay to help people with jerky mouse movements(like those using head / eye trackers)
        this.#mouseOutTimer = setTimeout(function () {
            _this.hideMenu();
        }, this.#mouseOutHideDelay);
    }

    #mouseover(e) {
        this.showMenu();
        clearTimeout(this.#mouseOutTimer);
    }

    #toggleMenu(e) {
        e.preventDefault();

        if (this.#menuVisible) {
            this.hideMenu();
        } else {
            this.showMenu();
        }
    }

    #triggerKeydownHandler(e) {
        // Hide the menu when a keyboard user presses Space, Enter, Escape, or tabs out of it
        if (e.key === 'Enter' || e.keyCode === 32 || (e.key === 'Tab' && !e.shiftKey && e.target === this.#lastMenuItem) || (e.key === 'Tab' && e.shiftKey && e.target === this.#firstMenuItem) || e.key === 'Escape') {
            this.hideMenu();
            if (e.keyCode === 32) {
                $(e.target).trigger('click');
                e.preventDefault();
            }
            // Move focus back to the menu toggle button on Escape
            if (e.key == 'Escape') {
                this.#triggerButton.focus();
            }
        }
    }

};
